import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import isEqual from 'lodash/isEqual';
import { tableFormulas } from '@builttocreate/engine-utils';

import CustomModal from '../../../misc/Modal';
import ThemeIcons from '../../../../theme/Icons';
import FieldTypes from '../../../../constants/FieldTypes';
import ImageField from '../ImageField';
import SignatureField from '../SignatureField';
import BarcodeGenericField from '../BarcodeGenericField';
import BlockGenericField from '../BlockGenericField';
import FieldDateInput from '../FieldDateInput';
import { resolveColumnRequired } from '../../../../lib/requiredLogic';
import { resolveColumnCellHidden } from '../../../../lib/cellVisibilityLogic';
import { filterHiddenTableColumns } from '../../../../lib/tableHelper';

const { getCellEditValue, getColumnFormula } = tableFormulas;

const fileToDataUri = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

let _pendingIdCounter = 0;
const generatePendingId = () => `_pending_${Date.now()}_${++_pendingIdCounter}`;

// ===  Styled Components  ===

const StyledColumnSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 4px;
`;

const StyledImageFieldWrapper = styled.div`
  max-height: 220px;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const StyledSignatureFieldWrapper = styled.div`
  height: 150px;
`;

const StyledSectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #999;
  text-transform: uppercase;
  margin-bottom: 8px;
`;

const StyledFieldGroup = styled.div`
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const StyledInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 6px 8px;
  font-size: 13px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  outline: none;

  &:focus {
    border-color: #2196f3;
  }
`;

const StyledSelect = styled.select`
  width: 100%;
  box-sizing: border-box;
  padding: 6px 8px;
  font-size: 13px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  outline: none;
  background: #fff;

  &:focus {
    border-color: #2196f3;
  }
`;

const StyledCheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px;
`;

const StyledCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  cursor: pointer;
  padding: 6px 0;
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
  }
`;

// ===  Fill-mode cell editor per column type  ===

const FillCellEditor = ({ column, value, edited, timezone, onChange }) => {
  const colType = column.type;

  if (colType === 'dropdown') {
    const options = (column.options || []).filter((o) => !o.deleted);
    return (
      <StyledSelect
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="" />
        {options.map((opt) => (
          <option key={opt._id} value={opt._id}>{opt.value}</option>
        ))}
      </StyledSelect>
    );
  }

  if (colType === 'multiSelect') {
    const options = (column.options || []).filter((o) => !o.deleted);
    const selected = Array.isArray(value) ? value : [];
    const allowMultiple = column.multi !== false;
    const handleToggle = (optId) => {
      let next;
      if (allowMultiple) {
        next = selected.includes(optId)
          ? selected.filter((id) => id !== optId)
          : [...selected, optId];
      } else {
        // Single-choice: clear only when re-clicking the ONLY selection; otherwise
        // keep just the clicked one (legacy multi-valued cells collapse, not clear).
        next = (selected.length <= 1 && selected.includes(optId)) ? [] : [optId];
      }
      onChange(next);
    };
    return (
      <StyledCheckboxGroup>
        {options.map((opt) => (
          <StyledCheckboxLabel key={opt._id}>
            <input
              type="checkbox"
              checked={selected.includes(opt._id)}
              onChange={() => handleToggle(opt._id)}
            />
            {opt.value}
          </StyledCheckboxLabel>
        ))}
      </StyledCheckboxGroup>
    );
  }

  if (colType === 'number') {
    return (
      <StyledInput
        type="number"
        value={value != null ? value : ''}
        onChange={(e) => {
          const val = e.target.value;
          if (val === '') {
            onChange('');
          } else {
            onChange(val.indexOf('.') === -1 ? parseInt(val, 10) : parseFloat(val));
          }
        }}
      />
    );
  }

  if (colType === 'date') {
    return (
      <FieldDateInput
        disabled={false}
        value={value}
        timezone={timezone}
        enableTimezoneAnchor={true}
        format={column.format || 'MM/DD/YYYY'}
        primaryColor="#2196f3"
        fontSize={13}
        fontColor="#444"
        borderColor="#e0e0e0"
        borderRadius={4}
        borderWidth={1}
        backgroundColor="#fff"
        padding={6}
        onChange={onChange}
      />
    );
  }

  if (colType === 'text') {
    //NO-2347: until the user edits this box it shows the edit value (a blank cell under a column formula
    //shows that formula, a stored 0 shows 0), matching the grid and input group editors. Once edited it
    //shows the draft as typed, so emptying it stays empty instead of snapping the formula back in.
    const shownValue = edited ? value : getCellEditValue(value, column);
    const displayValue = shownValue != null ? String(shownValue) : '';

    //A hint only, never part of the value: an empty box falls back to the column formula
    const columnFormula = getColumnFormula(column);
    const placeholder = edited && displayValue === '' && columnFormula ? columnFormula : undefined;

    return (
      <StyledInput
        type="text"
        value={displayValue}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  const displayValue = value != null ? String(value) : '';
  return (
    <StyledInput
      type="text"
      value={displayValue}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

FillCellEditor.propTypes = {
  column: PropTypes.object.isRequired,
  value: PropTypes.any,
  edited: PropTypes.bool,
  timezone: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

// ===  Main Modal Component  ===

const TableFieldEditRowModal = ({
  isOpen,
  row,
  tableColumns,
  fieldPositionTableColumns,
  docFields,
  view,
  fieldMode,
  onCellChange,
  onCellBatchChange,
  onCellFileUpload,
  onCellFileBatchUpload,
  onCellFileClick,
  onCellFileDelete,
  onCapture,
  enableSystemFilePicker,
  onClose,
}) => {

  const [showConfirm, setShowConfirm] = useState(false);
  const prevRowCellsRef = useRef({});
  const [draftCells, setDraftCells] = useState({});

  /**
   * NO-2347: columns the user has edited since the draft was last reset. Lives beside the draft (not
   * in the text editor) so it survives an editor unmounting under cell visibility logic, and is reset
   * in the same places the draft is.
   */
  const [editedColumnIds, setEditedColumnIds] = useState({});
  const [draftTimezone, setDraftTimezone] = useState(null);
  const draftSignerRef = useRef(null);
  const pendingUploadsRef = useRef({});

  // ===  Pending cell changes  ===

  useEffect(() => {
    if (!isOpen) return;
    prevRowCellsRef.current = row?.cells ? { ...row.cells } : {};
    setDraftCells(row?.cells ? { ...row.cells } : {});
    setEditedColumnIds({}); //Fresh draft, fresh edit flags
    setDraftTimezone(row?.tz || null);
  }, [isOpen, row?._id, row?.tz]);

  useEffect(() => {
    if (!isOpen || !row?.cells) return;

    //Worked out once, outside the updaters, because both the draft and the edit flags need the same list
    const previousRowCells = prevRowCellsRef.current || {};
    const overwrittenIds = Object.keys(row.cells)
      .filter((columnId) => !isEqual(previousRowCells[columnId], row.cells[columnId]));

    prevRowCellsRef.current = { ...row.cells };

    if (overwrittenIds.length === 0) return;

    setDraftCells((currentDraftCells) => {
      const nextDraftCells = { ...currentDraftCells };
      overwrittenIds.forEach((columnId) => { nextDraftCells[columnId] = row.cells[columnId]; });
      return nextDraftCells;
    });

    //NO-2347: the draft for these columns is the stored value again, so they edit as fresh boxes
    setEditedColumnIds((currentIds) => {
      if (!overwrittenIds.some((columnId) => currentIds[columnId])) return currentIds;
      const nextIds = { ...currentIds };
      overwrittenIds.forEach((columnId) => { delete nextIds[columnId]; });
      return nextIds;
    });
  }, [isOpen, row?.cells]);

  const draftCellsRef = useRef(draftCells);

  draftCellsRef.current = draftCells;

  const activeColumns = useMemo(
    () => filterHiddenTableColumns(tableColumns || [], fieldPositionTableColumns, view, fieldMode, docFields)
      .filter((col) => !col.deleted)
      .filter((col) => !resolveColumnCellHidden(col, draftCells, docFields)),
    [tableColumns, fieldPositionTableColumns, view, fieldMode, docFields, draftCells]
  );

  const hasPendingChanges = useCallback(() => {
    const hasPendingUploads = Object.keys(pendingUploadsRef.current).length > 0;
    return hasPendingUploads || !isEqual(draftCellsRef.current, row?.cells || {});
  }, [row?.cells]);

  const resetState = useCallback(() => {
    prevRowCellsRef.current = row?.cells ? { ...row.cells } : {};
    draftSignerRef.current = null;
    pendingUploadsRef.current = {};
    setDraftCells(row?.cells ? { ...row.cells } : {});
    setEditedColumnIds({});
    setDraftTimezone(row?.tz || null);
  }, [row?.cells, row?.tz]);

  // ===  Cell change handler  ===

  const handleFillCellChange = useCallback((columnId, value, timezone) => {
    setDraftCells((currentDraftCells) => ({
      ...currentDraftCells,
      [columnId]: value,
    }));
    //Only the text editor reads this flag, so marking every column type is harmless
    setEditedColumnIds((currentIds) => (currentIds[columnId] ? currentIds : { ...currentIds, [columnId]: true }));
    if (timezone) setDraftTimezone(timezone);
  }, []);

  // ===  Image upload handler (deferred)  ===

  const handleImageFileUpload = useCallback(async (colId, multi, files) => {

    const fileList = Array.isArray(files) ? files : [files];

    const tempUrlObjects = [];

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      const dataUri = await fileToDataUri(file);
      tempUrlObjects.push({
        _id: generatePendingId(),
        url: dataUri,
        fileName: file.name,
        _pending: true,
      });
    }

    if (multi) {
      const existing = pendingUploadsRef.current[colId] || [];
      pendingUploadsRef.current[colId] = [...existing, ...fileList];
    } else {
      pendingUploadsRef.current[colId] = [fileList[0]];
    }

    setDraftCells((currentDraftCells) => {
      const existingValue = currentDraftCells[colId];

      let nextValue;

      if (multi) {
        const existingArray = Array.isArray(existingValue) ? existingValue : [];
        nextValue = [...existingArray, ...tempUrlObjects];
      } else {
        nextValue = [tempUrlObjects[0]];
      }

      return { ...currentDraftCells, [colId]: nextValue };
    });

  }, []);

  const handleImageFileDelete = useCallback((colId, multi, urlObject, urlIndex) => {

    if (urlObject && urlObject._pending) {

      const pendingFiles = pendingUploadsRef.current[colId];

      if (pendingFiles) {

        setDraftCells((currentDraftCells) => {
          const existingValue = Array.isArray(currentDraftCells[colId]) ? currentDraftCells[colId] : [];
          const pendingItems = existingValue.filter((item) => item._pending);
          const pendingIdx = pendingItems.findIndex((item) => item._id === urlObject._id);

          if (pendingIdx !== -1 && pendingFiles.length > pendingIdx) {
            const nextFiles = [...pendingFiles];
            nextFiles.splice(pendingIdx, 1);

            if (nextFiles.length === 0) {
              delete pendingUploadsRef.current[colId];
            } else {
              pendingUploadsRef.current[colId] = nextFiles;
            }
          }

          const nextValue = existingValue.filter((item) => item._id !== urlObject._id);
          return { ...currentDraftCells, [colId]: nextValue };
        });

      }

      return;
    }

    setDraftCells((currentDraftCells) => {
      const existingValue = Array.isArray(currentDraftCells[colId]) ? currentDraftCells[colId] : [];
      const nextValue = [...existingValue];
      nextValue.splice(urlIndex, 1);
      return { ...currentDraftCells, [colId]: nextValue };
    });

  }, []);

  // ===  Save  ===

  const handleSave = useCallback(() => {

    if (!hasPendingChanges()) {
      resetState();
      onClose();
      return;
    }

    const nextCells = draftCellsRef.current || {};

    const currentCells = row?.cells || {};

    const changedIds = Array.from(new Set([...Object.keys(currentCells), ...Object.keys(nextCells)]));

    const nextTimezone = draftTimezone || row?.tz;

    const pendingUploadColumnIds = Object.keys(pendingUploadsRef.current);

    const batchedCells = {};

    let hasDateColumn = false;

    for (let i = 0; i < changedIds.length; i++) {

      const colId = changedIds[i];

      if (pendingUploadColumnIds.includes(colId)) continue;

      if (!isEqual(nextCells[colId], currentCells[colId])) {

        const cellValue = nextCells[colId];
        const cleanedValue = Array.isArray(cellValue)
          ? cellValue.filter((item) => !item?._pending)
          : cellValue;

        batchedCells[colId] = cleanedValue;
        const column = (tableColumns || []).find((nextColumn) => nextColumn._id === colId);
        if (column?.type === 'date') hasDateColumn = true;
      }

    }

    const timezone = hasDateColumn ? nextTimezone : undefined;
    const signer = draftSignerRef.current || undefined;

    if (pendingUploadColumnIds.length > 0) {

      const columnUploads = {};

      pendingUploadColumnIds.forEach((colId) => {
        const draftValue = nextCells[colId];
        const existingValue = Array.isArray(draftValue)
          ? draftValue.filter((item) => !item?._pending)
          : [];

        columnUploads[colId] = {
          files: pendingUploadsRef.current[colId],
          existingValue,
        };
      });

      if (onCellFileBatchUpload) {
        onCellFileBatchUpload(row._id, columnUploads, () => {}, () => {}, batchedCells, timezone, signer);
      } else if (onCellFileUpload) {
        if (Object.keys(batchedCells).length > 0) {
          if (onCellBatchChange) {
            onCellBatchChange(row._id, batchedCells, timezone, signer);
          }
        }
        pendingUploadColumnIds.forEach((colId) => {
          onCellFileUpload(row._id, colId, columnUploads[colId].files, () => {}, () => {});
        });
      }

    } else if (Object.keys(batchedCells).length > 0) {

      if (onCellBatchChange) {
        onCellBatchChange(row._id, batchedCells, timezone, signer);
      } else if (onCellChange) {
        Object.keys(batchedCells).forEach((colId) => {
          const column = (tableColumns || []).find((nextColumn) => nextColumn._id === colId);
          if (column?.type === 'date') {
            onCellChange(row._id, colId, batchedCells[colId], nextTimezone, signer);
          } else {
            onCellChange(row._id, colId, batchedCells[colId], undefined, signer);
          }
        });
      }

    }

    resetState();

    onClose();

  }, [hasPendingChanges, draftTimezone, onCellChange, onCellBatchChange, onCellFileUpload, onCellFileBatchUpload, onClose, resetState, row, tableColumns]);

  // ===  Close / Discard  ===

  const handleCloseAttempt = useCallback(() => {

    if (hasPendingChanges()) {
      setShowConfirm(true);
    } else {
      resetState();
      onClose();
    }

  }, [hasPendingChanges, onClose, resetState]);

  const handleDiscard = useCallback(() => {
    resetState();
    setShowConfirm(false);
    onClose();
  }, [onClose, resetState]);

  const handleConfirmSave = useCallback(() => {
    setShowConfirm(false);
    handleSave();
  }, [handleSave]);

  // ===  Render  ===

  const renderNativeField = (column, cellValue) => {
    const colType = column.type;
    const colId = column._id;
    const rowId = row?._id;

    if (colType === 'image') {
      return (
        <StyledImageFieldWrapper>
          <ImageField
            type={FieldTypes.image}
            multi={column.multi}
            rteEmbedded
            disabled={false}
            value={cellValue}
            fieldId={colId}
            enableSystemFilePicker={enableSystemFilePicker}
            primaryMaxWidth={column.maxImageWidth || 200}
            primaryMaxHeight={column.maxImageHeight || 200}
            dangerColor="#e74c3c"
            borderWidth={1}
            borderColor="#e0e0e0"
            borderRadius={4}
            onFileUpload={(f, i, p, files, loadingCallback, errorCallback) => {
              loadingCallback(true);
              handleImageFileUpload(colId, column.multi, files).then(() => {
                loadingCallback(false);
              }).catch(() => {
                loadingCallback(false);
                if (errorCallback) errorCallback(true);
              });
            }}
            onFileClick={(f, i, p, urlObject, urlIndex) => {
              if (onCellFileClick) onCellFileClick(rowId, colId, urlObject, urlIndex);
            }}
            onFileDelete={(f, i, p, urlObject, urlIndex) => {
              handleImageFileDelete(colId, column.multi, urlObject, urlIndex);
            }}
            onChange={(f, i, p, value) => {
              handleFillCellChange(colId, value);
            }}
            padding={2}
          />
        </StyledImageFieldWrapper>
      );
    }

    if (colType === 'signature') {
      return (
        <StyledSignatureFieldWrapper>
          <SignatureField
            type={colType}
            fieldId={colId}
            disabled={false}
            value={cellValue}
            borderWidth={1}
            borderColor="#e0e0e0"
            borderRadius={4}
            onCapture={onCapture}
            onChange={(f, i, p, value, signer) => {
              handleFillCellChange(colId, value);
              if (signer) draftSignerRef.current = signer;
            }}
            maxImageWidth={column.maxImageWidth}
            maxImageHeight={column.maxImageHeight}
          />
        </StyledSignatureFieldWrapper>
      );
    }

    if (colType === 'barcode') {
      return (
        <BarcodeGenericField
          type={colType}
          fieldId={colId}
          disabled={false}
          value={cellValue}
          wrapperStyles={{ height: 'auto' }}
          borderWidth={1}
          borderColor="#e0e0e0"
          borderRadius={4}
          onCapture={onCapture}
          onChange={(f, i, p, value) => {
            handleFillCellChange(colId, value);
          }}
        />
      );
    }

    if (colType === 'block') {
      return (
        <BlockGenericField
          type={colType}
          fieldId={colId}
          disabled={false}
          fieldMode={fieldMode}
          value={cellValue}
          borderWidth={1}
          borderColor="#e0e0e0"
          borderRadius={4}
          onChange={(f, i, p, value) => {
            handleFillCellChange(colId, value);
          }}
        />
      );
    }

    return null;
  };

  const isNativeFieldType = (colType) => {
    return colType === 'image' || colType === 'signature' || colType === 'barcode' || colType === 'block';
  };

  const renderFillModeContent = () => {
    return activeColumns.map((column) => {
      const colId = column._id;
      const cellValue = draftCells[colId];
      const fieldPositionColumn = fieldPositionTableColumns && fieldPositionTableColumns[colId] ? fieldPositionTableColumns[colId] : {};
      const resolvedColumn = fieldPositionColumn.format ? { ...column, format: fieldPositionColumn.format } : column;

      if (isNativeFieldType(column.type)) {
        return (
          <StyledColumnSection key={colId}>
            <StyledSectionLabel>
              {column.title || colId}
              {resolveColumnRequired(column, draftCells, docFields) ? <span style={{ color: 'red', fontSize: '16px', verticalAlign: 'middle', lineHeight: 1 }}> *</span> : null}
            </StyledSectionLabel>
            <StyledFieldGroup>
              {renderNativeField(column, cellValue)}
            </StyledFieldGroup>
          </StyledColumnSection>
        );
      }

      return (
        <StyledColumnSection key={colId}>
          <StyledSectionLabel>
            {column.title || colId}
            {resolveColumnRequired(column, draftCells, docFields) ? <span style={{ color: 'red', fontSize: '16px', verticalAlign: 'middle', lineHeight: 1 }}> *</span> : null}
          </StyledSectionLabel>
          <StyledFieldGroup>
            <FillCellEditor
              column={resolvedColumn}
              value={cellValue}
              edited={!!editedColumnIds[colId]}
              timezone={draftTimezone || row?.tz}
              onChange={(val, timezone) => handleFillCellChange(colId, val, timezone)}
            />
          </StyledFieldGroup>
        </StyledColumnSection>
      );
    });
  };

  if (!isOpen && !showConfirm) return null;

  return (
    <>
      <CustomModal
        icon={ThemeIcons.settings.edit}
        title="Edit Row"
        isOpen={isOpen}
        width={700}
        height={600}
        submitText="Save"
        submitDisabled={false}
        onSubmit={handleSave}
        cancelText="Close"
        onCancel={handleCloseAttempt}
        onHide={handleCloseAttempt}
      >
        {renderFillModeContent()}
      </CustomModal>
      <CustomModal
        title="Unsaved Changes"
        isOpen={showConfirm}
        width={340}
        height={120}
        submitText="Save"
        onSubmit={handleConfirmSave}
        cancelText="Discard"
        onCancel={handleDiscard}
        onHide={handleDiscard}
      >
        <span>You have unsaved changes. Would you like to save or discard them?</span>
      </CustomModal>
    </>
  );

};

TableFieldEditRowModal.propTypes = {
  isOpen: PropTypes.bool,
  row: PropTypes.object,
  tableColumns: PropTypes.array,
  fieldPositionTableColumns: PropTypes.object,
  docFields: PropTypes.array,
  view: PropTypes.string,
  fieldMode: PropTypes.string,
  onCellChange: PropTypes.func,
  onCellBatchChange: PropTypes.func,
  onCellFileUpload: PropTypes.func,
  onCellFileBatchUpload: PropTypes.func,
  onCellFileClick: PropTypes.func,
  onCellFileDelete: PropTypes.func,
  onCapture: PropTypes.func,
  enableSystemFilePicker: PropTypes.bool,
  onClose: PropTypes.func,
};

export default TableFieldEditRowModal;
